// Port of `Arrangement` (SuiteModel/ModelTypes.swift): parent-pointer + fractional-index nodes read as an ordered tree,
// with missing parents and cycles from concurrent moves promoted to the root, exactly as `tree()` does.
import type { Arrangement, ArrangementNode } from './documents.ts';
import { swiftCompare } from './order.ts';

/** `TreeNode` as the Swift builds it. */
export interface ArrangementTreeNode {
  node: ArrangementNode;
  children: ArrangementTreeNode[];
  /** The parent this node sits under in the tree (cycle-safe); `node.parentID` is the raw pointer. */
  parentID?: string;
}

const byPosition = (a: ArrangementNode, b: ArrangementNode) => swiftCompare(a.position, b.position) || swiftCompare(a.id, b.id);

/** `resolvedParent(of:)`: the parent as `tree()` places it; undefined for a missing parent or where a cycle is broken. */
export function resolvedParent(a: Arrangement, node: ArrangementNode): string | undefined {
  const parent = node.parentID;
  if (parent === undefined || !a.nodes.has(parent)) return undefined;
  const seen = new Set([node.id]);
  let cursor: string | undefined = parent;
  while (cursor !== undefined) {
    if (seen.has(cursor)) return undefined;
    seen.add(cursor);
    const up: string | undefined = a.nodes.get(cursor)?.parentID;
    cursor = up !== undefined && a.nodes.has(up) ? up : undefined;
  }
  return parent;
}

/** `tree()`: children sorted by (position, id). */
export function tree(a: Arrangement): ArrangementTreeNode[] {
  const children = new Map<string | undefined, ArrangementNode[]>();
  for (const node of a.nodes.values()) {
    const p = resolvedParent(a, node);
    const list = children.get(p);
    if (list) list.push(node); else children.set(p, [node]);
  }
  const build = (parent: string | undefined): ArrangementTreeNode[] =>
    (children.get(parent) ?? []).sort(byPosition)
      .map((node) => ({ node, children: build(node.id), ...(parent !== undefined ? { parentID: parent } : {}) }));
  return build(undefined);
}

/** Depth-first reading order over a built tree. */
export function* walkTree(nodes: ArrangementTreeNode[], depth = 0): Generator<{ tree: ArrangementTreeNode; depth: number }> {
  for (const n of nodes) {
    yield { tree: n, depth };
    yield* walkTree(n.children, depth + 1);
  }
}

/** `orderedFragmentIDs()`: fragment ids in depth-first reading order. */
export function orderedFragmentIDs(a: Arrangement, built = tree(a)): string[] {
  const out: string[] = [];
  for (const { tree: n } of walkTree(built)) if (n.node.fragmentID !== undefined) out.push(n.node.fragmentID);
  return out;
}

/** `flattened()`: every node in reading order with its depth. */
export function flattened(a: Arrangement): { node: ArrangementNode; depth: number }[] {
  return [...walkTree(tree(a))].map(({ tree: t, depth }) => ({ node: t.node, depth }));
}

/** `ancestors(of:)`: nearest first, as `tree()` resolves them. */
export function ancestors(a: Arrangement, nodeID: string): ArrangementNode[] {
  const out: ArrangementNode[] = [];
  const seen = new Set([nodeID]);
  const parentOf = (id: string) => {
    const n = a.nodes.get(id);
    const p = n ? resolvedParent(a, n) : undefined;
    return p !== undefined ? a.nodes.get(p) : undefined;
  };
  let cursor = parentOf(nodeID);
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    out.push(cursor);
    cursor = parentOf(cursor.id);
  }
  return out;
}

/** `TreeNode` folders: a folder node, or anything with children (`isFolder || !children.isEmpty`). */
export const isFolderish = (t: ArrangementTreeNode) => t.node.isFolder || t.children.length > 0;
